import type {
  Lesson,
  RepoState,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
} from "../../lib/types";

export const LESSON_COMMITS: Lesson = {
  courseId: "commits-branches",
  courseTitle: "Commits & branches",
  id: "lesson-1",
  title: "Commits & branches",
  subtitle: "Lesson 2 of 7",
  steps: [
    {
      id: "intro",
      shortTitle: "What is a commit?",
      shortSub: "Mental model",
      eyebrow: "Step 1 of 6",
      title: "A commit is a snapshot.",
      sub: "Every time you commit, git takes a photo of your project — every file, exactly as it is — and labels it with a unique ID. The history of your project is just a chain of those photos.",
      kind: "intro",
    },
    {
      id: "edit",
      shortTitle: "Make a change",
      shortSub: "Working tree → staging",
      eyebrow: "Step 2 of 6",
      title: "Edit a file. Then stage it.",
      sub: "Your edits start out in the working tree — git knows the file changed, but it hasn't been told to include the change in the next commit yet. Staging is how you say \"this one's ready.\"",
      kind: "sandbox",
      goal: "stage",
      explainer: {
        heading: "Step 1 of 2: staging",
        paragraphs: [
          "Between your editor and git's history sits a holding area called the <b>staging area</b> (sometimes called the <em>index</em>). Every change goes through it on the way to becoming a commit.",
          "Why the extra step? Because in real projects you often change five files but only want to commit two of them right now. Staging lets you pick what goes in the next snapshot, instead of forcing you to commit everything every time.",
          "<b>Try it:</b> change some of the code in <code>App.jsx</code> on the right. The file will turn yellow (Modified). Then click the <b>git add .</b> button to stage it — the file turns green (Staged).",
        ],
        commands: [
          {
            cmd: "git status",
            what: "Show which files are modified, staged, or untracked. The first command most people run.",
          },
          {
            cmd: "git add <file>",
            what: "Stage one specific file — mark it for inclusion in the next commit.",
          },
          {
            cmd: "git add .",
            what: 'Stage every modified file in the current folder. The "." means "everything here." Convenient when you do want to commit it all.',
          },
        ],
      },
    },
    {
      id: "commit",
      shortTitle: "Make your first commit",
      shortSub: "Snapshot it",
      eyebrow: "Step 3 of 6",
      title: "Now commit.",
      sub: 'Staging said "these are the changes I want in the next snapshot." Commit actually takes the snapshot, attaches your message, gives it a unique ID, and adds it to the chain.',
      kind: "sandbox",
      goal: "commit",
      explainer: {
        heading: "Step 2 of 2: commit",
        paragraphs: [
          "A <b>commit</b> is a permanent, labeled snapshot of everything currently staged. Once you commit, that snapshot lives in your repository's history forever — you can always come back to it, even if you delete files later.",
          "Every commit needs a <b>message</b>: a short sentence explaining what changed. \"Update homepage heading\" is good. \"stuff\" is not. Future-you will read these messages when something breaks and you need to figure out when it broke.",
          "Watch the graph on the right when you click <b>git commit</b> — a new dot appears at the tip of <code>main</code>, with a fresh 7-character ID. That's your commit.",
        ],
        commands: [
          {
            cmd: 'git commit -m "message"',
            what: "Take a snapshot of everything currently staged, label it with your message, and add it to history.",
          },
          {
            cmd: "git log --oneline",
            what: "Show the chain of commits, newest first — one line per commit.",
          },
        ],
      },
    },
    {
      id: "branch",
      shortTitle: "Branch off",
      shortSub: "Parallel work",
      eyebrow: "Step 4 of 6",
      title: "Create a branch.",
      sub: "A branch lets you work on something risky or experimental without disturbing main. The cool part: it's not a copy of your files — it's just a label that points at a commit.",
      kind: "sandbox",
      goal: "branch-and-commit",
      explainer: {
        heading: "Branches are labels, not copies",
        paragraphs: [
          "When you create a branch in git, <em>no files are duplicated</em>. A branch is just a movable sticky-note that points at a specific commit. That's why branching in git is essentially free — it's a single line in a text file.",
          "<b>HEAD</b> is git's term for \"the branch you're currently on.\" When you commit, the commit goes on whichever branch HEAD points to, and HEAD slides forward to the new commit.",
          "<b>Try it:</b> click <b>git switch -c feature</b> to create a new branch called <code>feature</code> and switch to it. Then edit <code>App.jsx</code> again, stage, and commit. You'll see the new commit appear on a separate line in the graph — <code>main</code> stays where it was.",
        ],
        commands: [
          {
            cmd: "git branch",
            what: "List all branches in this repo. The current one has a * next to it.",
          },
          {
            cmd: "git switch <name>",
            what: "Switch to an existing branch. Your working files update to match it.",
          },
          {
            cmd: "git switch -c <name>",
            what: 'The "-c" means "create." Makes a new branch at your current commit and switches to it in one step.',
          },
        ],
      },
    },
    {
      id: "merge",
      shortTitle: "Merge it back",
      shortSub: "Bring branches together",
      eyebrow: "Step 5 of 6",
      title: "Merge feature into main.",
      sub: "You did the experimental work on feature, you're happy with it, now you want main to include it. That's a merge.",
      kind: "sandbox",
      goal: "merge",
      explainer: {
        heading: "Switch first, then merge",
        paragraphs: [
          "Merge has a direction: you merge <em>from</em> one branch <em>into</em> the one you're currently on. So step one is always to switch onto the branch that should receive the changes — in this case, <code>main</code>.",
          "Because <code>main</code> hasn't moved while you were off building on <code>feature</code>, git can do the simplest possible merge: just slide the <code>main</code> label forward to the same commit <code>feature</code> points at. No new merge commit is created. This shortcut is called a <b>fast-forward</b>.",
          "<b>Try it:</b> click <b>git switch main</b> to move HEAD back to main, then <b>git merge feature</b>. Watch the <code>main</code> label hop forward in the graph.",
        ],
        commands: [
          {
            cmd: "git switch main",
            what: "Move HEAD back to main so that's the branch receiving the merge.",
          },
          {
            cmd: "git merge feature",
            what: "Bring feature's commits into the current branch. If possible, git fast-forwards instead of creating a merge commit.",
          },
        ],
      },
    },
    {
      id: "review",
      shortTitle: "See it on GitHub",
      shortSub: "The PR view",
      eyebrow: "Step 6 of 6",
      title: "Here's what that looks like on GitHub.",
      sub: "On a real team you'd push your branch and open a Pull Request before merging. Same commits, same diff — wrapped in a review UI.",
      kind: "github",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "You commit, branch, and merge.",
      sub: "Quick check below. Then a cheatsheet and glossary you can come back to anytime.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    q: "When you 'commit' something, what exactly does git save?",
    options: [
      { t: "Just the lines you changed", correct: false },
      { t: "A snapshot of every file in your project", correct: true },
      { t: "Only the files you opened today", correct: false },
      { t: "A backup zip of the folder", correct: false },
    ],
    why: "Right — each commit is a complete snapshot of every file at that moment. Git stores it efficiently, so it doesn't waste space, but conceptually it's the whole project.",
    wrongWhy:
      "Not quite. Each commit is a snapshot of the whole project, not just the lines you changed. Git is efficient about how it stores them, but conceptually every commit captures every file.",
  },
  {
    q: "What is a branch?",
    options: [
      { t: "A separate copy of the entire repo's files", correct: false },
      { t: "A movable label that points at a commit", correct: true },
      { t: "A folder where parallel work goes", correct: false },
      { t: "A backup taken before risky changes", correct: false },
    ],
    why: "Exactly. A branch is just a label (a pointer). Creating one doesn't copy any files — that's why branching in git is so cheap and fast.",
    wrongWhy:
      "Branches feel like copies, but they're actually just lightweight labels that point at a specific commit. No files are duplicated when you create one.",
  },
  {
    q: "You merged feature into main and the main label moved forward without creating a new merge commit. What happened?",
    options: [
      { t: "A bug — you should re-do the merge", correct: false },
      {
        t: "Fast-forward merge — git just slid the label forward",
        correct: true,
      },
      { t: "The branch got deleted automatically", correct: false },
      { t: "Git skipped a step", correct: false },
    ],
    why: "Yes — that's a fast-forward. Since main hadn't moved while feature was being built, git could just slide main's label up to where feature was. Clean, no merge commit needed.",
    wrongWhy:
      "Actually that's the cleanest possible merge — called a fast-forward. Because main hadn't moved, git just moved its label forward to where feature was. No new commit needed.",
  },
];

export const GLOSSARY: GlossaryItem[] = [
  { term: "Repo", def: "Short for repository — your project's folder, plus all the history git tracks for it." },
  { term: "Commit", def: "A snapshot of every file in the repo at a moment in time, with a unique ID." },
  { term: "Working tree", def: "The files on your laptop as you see them right now, including unsaved edits." },
  { term: "Staging area", def: "A holding pen for changes you want to include in your next commit." },
  { term: "Branch", def: "A movable label pointing at a commit. Lets you work on changes without disturbing main." },
  { term: "HEAD", def: "The branch (or commit) you currently have checked out — what your edits affect." },
  { term: "main", def: "By convention, the primary branch where the canonical version of the project lives." },
  { term: "Merge", def: "Combining the work from one branch into another." },
  { term: "Fast-forward", def: "A merge where the target branch hadn't moved, so git just slides the label forward." },
  { term: "SHA", def: "The unique 40-character ID of a commit. Usually shown as just the first 7 characters." },
];

export const CHEATSHEET: CheatsheetItem[] = [
  { cmd: "git status", desc: "What state are my files in right now?" },
  { cmd: "git add <file>", desc: "Stage a file for the next commit." },
  { cmd: "git add .", desc: "Stage everything that changed." },
  { cmd: 'git commit -m "msg"', desc: "Make a commit with a message." },
  { cmd: "git log --oneline", desc: "Show the commit history." },
  { cmd: "git branch", desc: "List branches." },
  { cmd: "git branch <name>", desc: "Create a new branch at HEAD." },
  { cmd: "git switch <name>", desc: "Switch to a different branch." },
  { cmd: "git switch -c <name>", desc: "Create a branch and switch to it." },
  { cmd: "git merge <name>", desc: "Merge the named branch into the current one." },
  { cmd: "git diff", desc: "See what changed but is not yet staged." },
  { cmd: "git diff --staged", desc: "See what is staged for the next commit." },
];

const APP_JSX = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>Welcome to Atlas</h1>
        <p>Your Business Operating System.</p>
      </main>
    </div>
  );
}
`;

const HEADER_JSX = `export default function Header() {
  return (
    <header className="header">
      <img src="/logo.png" alt="logo" />
      <nav>
        <a href="/">Home</a>
        <a href="/about">About</a>
      </nav>
    </header>
  );
}
`;

const STYLES_CSS = `.app { font-family: system-ui; }
.header { padding: 16px; border-bottom: 1px solid #eee; }
.header nav { display: flex; gap: 16px; }
`;

export const INITIAL_REPO: RepoState = {
  commits: [
    { id: "a1b2c3d", msg: "Initial commit", branch: "main", parent: null, x: 0 },
    { id: "e4f5a6b", msg: "Add Header component", branch: "main", parent: "a1b2c3d", x: 1 },
    { id: "c7d8e9f", msg: "Wire up homepage layout", branch: "main", parent: "e4f5a6b", x: 2 },
  ],
  branches: { main: "c7d8e9f" },
  head: "main",
  files: {
    "App.jsx": { content: APP_JSX, committed: APP_JSX, staged: null, status: "clean" },
    "Header.jsx": { content: HEADER_JSX, committed: HEADER_JSX, staged: null, status: "clean" },
    "styles.css": { content: STYLES_CSS, committed: STYLES_CSS, staged: null, status: "clean" },
  },
};

export const INITIAL_LOG = [
  { kind: "cmd" as const, text: "git log --oneline" },
  { kind: "out" as const, text: "c7d8e9f Wire up homepage layout" },
  { kind: "out" as const, text: "e4f5a6b Add Header component" },
  { kind: "out" as const, text: "a1b2c3d Initial commit" },
];
